/**
 * <half-half-v2> — V2 PDP half / half firmness add-on
 * (snippets/half-half-v2.liquid + snippets/half-half-modal-v2.liquid).
 *
 * State: on/off (toggle tile, aria-pressed) + the selected firmness radio.
 * The half-layer variant is resolved from the embedded [data-hh-variants]
 * JSON by (main product size, firmness). On the document `variant:change`
 * (component-configuration-picker-v2.js) the size is re-read from
 * variant.options[data-size-position - 1]; combinations with no variant for
 * the new size are hidden, the selection falls back to the first available
 * one, and the whole block hides if the size has no half layers at all.
 *
 * Exposes window.V2HalfHalf.getItems() -> [{ variantId, qty }] and dispatches
 * document `halfhalf:change`, detail: { items, totalPrice } (integer cents),
 * which <buy-buttons-v2> folds into the ATC label and bundled submit — the
 * same contract as window.V2Addons / `addons:change`.
 *
 * Size / colour changes swap the whole .mp-v2 section
 * (component-configuration-picker-v2.js refetchVariant), which disconnects
 * this element and connects a fresh server-rendered one. The on/off state and
 * firmness are carried across the swap in `carried` so the shopper's choice
 * survives a size change; the fresh element re-announces itself on a
 * microtask so the also-fresh <buy-buttons-v2> (connected after this element,
 * it sits later in the DOM) picks the total up.
 *
 * The dialog's [data-hh-modal-add] button ticks the add-on and closes the
 * dialog; it is hidden while the add-on is already on.
 */
(function () {
  if (customElements.get('half-half-v2')) return;

  var carried = null;

  window.V2HalfHalf = window.V2HalfHalf || {
    getItems: function () {
      return [];
    },
  };

  function formatMoney(cents, format) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(cents, format).replace(/\.00(?=\D*$)/, '');
    }
    var amount = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
    return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  customElements.define(
    'half-half-v2',
    class extends HTMLElement {
      connectedCallback() {
        var json = this.querySelector('[data-hh-variants]');
        try {
          this.variants = json ? JSON.parse(json.textContent) : [];
        } catch (e) {
          this.variants = [];
        }
        this.toggle = this.querySelector('[data-hh-toggle]');
        this.optionsWrap = this.querySelector('[data-hh-options]');
        this.options = Array.prototype.slice.call(this.querySelectorAll('[data-hh-option]'));
        this.selectedWrap = this.querySelector('[data-hh-selected]');
        this.selectedName = this.querySelector('[data-hh-selected-name]');
        this.priceEl = this.querySelector('[data-hh-price]');
        this.sizePosition = parseInt(this.dataset.sizePosition, 10) || 1;
        this.dialog = document.getElementById(this.dataset.dialogId);

        this.on = false;
        this.size = this.dataset.initialSize || null;

        this._onVariantChange = this._onVariantChange.bind(this);
        this._onModalAdd = this._onModalAdd.bind(this);

        this.toggle.addEventListener('click', this._setOn.bind(this, null));
        this.optionsWrap.addEventListener('change', this._render.bind(this));
        document.addEventListener('variant:change', this._onVariantChange);
        document.addEventListener('click', this._onModalAdd);

        window.V2HalfHalf.getItems = this.getItems.bind(this);

        this._applySize();
        if (carried) this._restore(carried);
        carried = null;
        this._render();
        var self = this;
        Promise.resolve().then(function () {
          if (self.isConnected) self._dispatch();
        });
      }

      _restore(state) {
        var radio = state.firmness
          ? this.querySelector('[data-hh-radio][value="' + CSS.escape(state.firmness) + '"]')
          : null;
        if (radio && !radio.disabled) radio.checked = true;
        this.on = !!state.on && !this.hidden;
      }

      disconnectedCallback() {
        var checked = this._checkedRadio();
        carried = { on: this.on, firmness: checked ? checked.value : null };
        document.removeEventListener('variant:change', this._onVariantChange);
        document.removeEventListener('click', this._onModalAdd);
      }

      _variantFor(firmness) {
        var size = this.size;
        return (
          this.variants.filter(function (v) {
            return v.size === size && v.firmness === firmness;
          })[0] || null
        );
      }

      _checkedRadio() {
        return this.querySelector('[data-hh-radio]:checked');
      }

      _onVariantChange(event) {
        var variant = event.detail && event.detail.variant;
        if (!variant || !variant.options) return;
        var size = variant.options[this.sizePosition - 1];
        if (size === this.size) return;
        this.size = size;
        this._applySize();
        this._render();
      }

      // Show only combinations that exist for the current size; keep the
      // selection when possible, else fall back to the first available one.
      _applySize() {
        var self = this;
        var firstAvailable = null;
        this.options.forEach(function (opt) {
          var variant = self._variantFor(opt.dataset.firmness);
          var radio = opt.querySelector('[data-hh-radio]');
          opt.hidden = !variant;
          radio.disabled = !variant || !variant.available;
          if (variant && variant.available && !firstAvailable) firstAvailable = radio;
        });

        var checked = this._checkedRadio();
        if (!checked || checked.disabled) {
          if (checked) checked.checked = false;
          if (firstAvailable) firstAvailable.checked = true;
        }

        this.hidden = !firstAvailable;
        if (!firstAvailable) this.on = false;

        var priced = this._checkedRadio();
        var v = priced ? this._variantFor(priced.value) : null;
        if (v && this.priceEl) this.priceEl.textContent = '+' + formatMoney(v.price, this.dataset.moneyFormat);
      }

      _setOn(value) {
        this.on = value === null ? !this.on : !!value;
        this._render();
      }

      _onModalAdd(event) {
        var btn = event.target.closest('[data-hh-modal-add]');
        if (!btn || btn.dataset.hhFor !== this.dataset.dialogId) return;
        this._setOn(true);
        if (this.dialog && this.dialog.open) this.dialog.close();
      }

      _selectedVariant() {
        var radio = this._checkedRadio();
        return radio ? this._variantFor(radio.value) : null;
      }

      getItems() {
        var v = this.on ? this._selectedVariant() : null;
        return v && v.available ? [{ variantId: v.id, qty: 1 }] : [];
      }

      _render() {
        var variant = this._selectedVariant();
        this.toggle.setAttribute('aria-pressed', String(this.on));
        this.optionsWrap.hidden = !this.on;
        if (this.selectedWrap) this.selectedWrap.hidden = !(this.on && variant);
        if (this.selectedName) this.selectedName.textContent = variant ? variant.firmness : '';

        if (this.dialog) {
          var add = this.dialog.querySelector('[data-hh-modal-add]');
          var price = this.dialog.querySelector('[data-hh-modal-price]');
          if (add) add.hidden = this.on;
          if (price && variant) price.textContent = '+' + formatMoney(variant.price, this.dataset.moneyFormat);
        }

        this._dispatch();
      }

      _dispatch() {
        var variant = this._selectedVariant();
        var items = this.getItems();
        document.dispatchEvent(
          new CustomEvent('halfhalf:change', {
            detail: { items: items, totalPrice: items.length && variant ? variant.price : 0 },
          }),
        );
      }
    },
  );
})();
